import type { Permission } from '@/api/permissions';
import { type RoleRequestInput, RoleRequestType } from '@/api/role-requests/types';
import type { CatalogGroup, CatalogPermission } from '@/shared/modules/role-board/types';
import type { PermissionSelectionMap } from '@/utils/permission-catalog';

/**
 * Staged permission changes on one role, keyed by permission: `true` adds it, `false` takes it
 * off. Only rows that differ from what the role holds are kept, so an empty draft means the role
 * board has nothing to submit.
 */
export type PermissionDraft = ReadonlyMap<Permission, boolean>;

export const EMPTY_PERMISSION_DRAFT: PermissionDraft = new Map();

/**
 * Permissions that already have a request waiting for a decision. The backend refuses a second
 * request on the same permission, so a draft never stages one: it could not be sent or undone.
 */
export type PendingPermissions = { has(_permission: Permission): boolean };

const NO_PENDING: PendingPermissions = new Set<Permission>();

export type PermissionDraftChange = {
  permission: CatalogPermission;
  groupLabel: string;
};

export type PermissionDraftSummary = {
  added: PermissionDraftChange[];
  removed: PermissionDraftChange[];
};

export const toggleInPermissionDraft = (
  draft: PermissionDraft,
  saved: PermissionSelectionMap,
  permission: Permission,
  granted: boolean,
  pending: PendingPermissions = NO_PENDING,
): PermissionDraft => {
  if (saved.get(permission)?.locked || pending.has(permission)) return draft;

  const next = new Map(draft);
  if (saved.has(permission) === granted) next.delete(permission);
  else next.set(permission, granted);

  return next;
};

/** What the picker shows while a draft is open: the saved rows with the staged ones applied. */
export const applyPermissionDraft = (
  saved: PermissionSelectionMap,
  draft: PermissionDraft,
): PermissionSelectionMap => {
  if (!draft.size) return saved;

  const effective = new Map(saved);
  draft.forEach((granted, permission) => {
    if (granted) effective.set(permission, { locked: false });
    else effective.delete(permission);
  });

  return effective;
};

/**
 * Drops staged changes the role already reflects, e.g. after a refetch landed them, and changes
 * that now have a request waiting.
 */
export const prunePermissionDraft = (
  draft: PermissionDraft,
  saved: PermissionSelectionMap,
  pending: PendingPermissions = NO_PENDING,
): PermissionDraft => {
  const stale = [...draft].filter(
    ([permission, granted]) => saved.has(permission) === granted || pending.has(permission),
  );
  if (!stale.length) return draft;

  const next = new Map(draft);
  stale.forEach(([permission]) => next.delete(permission));

  return next;
};

export const countDraftChanges = (draft: PermissionDraft) => {
  const added = [...draft.values()].filter(Boolean).length;

  return { added, removed: draft.size - added, total: draft.size };
};

export const describePermissionDraft = (
  draft: PermissionDraft,
  groups: readonly CatalogGroup[],
): PermissionDraftSummary =>
  groups.reduce<PermissionDraftSummary>(
    (summary, group) => {
      group.permissions.forEach((permission) => {
        const granted = draft.get(permission.permission);
        if (granted === undefined) return;

        (granted ? summary.added : summary.removed).push({ permission, groupLabel: group.label });
      });

      return summary;
    },
    { added: [], removed: [] },
  );

/** The request API takes one permission per request, so a draft becomes one request per change. */
export const toPermissionChangeRequests = (
  roleId: string,
  draft: PermissionDraft,
  pending: PendingPermissions = NO_PENDING,
): RoleRequestInput[] =>
  [...draft]
    .filter(([permission]) => !pending.has(permission))
    .map(([permission, granted]) =>
      granted
        ? { type: RoleRequestType.AddRolePermission, payload: { roleId, permission } }
        : { type: RoleRequestType.RemoveRolePermission, payload: { roleId, permission } },
    );
